import React from 'react';
import '../styles/styles.css';
import './Text.css';

export type TextProps = {
  children: React.ReactNode;
  tag?: 'span' | 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p';
  className?: string;
  color?: 'text-primary' | 'text-secondary' | 'primary' | 'accent' | 'read-only' | 'positive' | 'negative' | 'informative' | 'notice';
  weight?: 'bold' | 'regular' | 'light';
  style?: React.CSSProperties;
  align?: 'left' | 'center' | 'right';
  nested?: boolean;
  width?: 'hug' | 'fill';
};

type TextComponent = React.FC<TextProps> & { displayName?: string };

const createTextComponent = (defaultClass: string): TextComponent => {
  const Component: TextComponent = ({
    width = 'fill',
    children,
    tag = 'p',
    className,
    color,
    weight,
    style,
    align,
  }) => {
    const Tag = tag as keyof JSX.IntrinsicElements;
    const combinedStyles: React.CSSProperties = {
      ...style,
    };

    const combinedClasses = [
      'text',
      color ? `color-${color}` : '',
      `width-${width}`,
      defaultClass,
      weight ? `weight-${weight}` : '',
      className,
      align === undefined ? '' : `align-${align}`
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <Tag className={combinedClasses} style={combinedStyles}>
        {children}
      </Tag>
    );
  };

  return Component;
};

// Helper function to generate components with displayName
const components = {
  Title1: 'title1',
  Title2: 'title2',
  Title3: 'title3',
  Headline: 'headline',
  Body: 'body',
  Subhead: 'subhead',
  Footnote: 'footnote',
  Caption1: 'caption1',
  Caption2: 'caption2',
};

export const {
  Title1,
  Title2,
  Title3,
  Headline,
  Body,
  Subhead,
  Footnote,
  Caption1,
  Caption2,
} = Object.entries(components).reduce((acc, [name, className]) => {
  const component = createTextComponent(className);
  component.displayName = name;
  acc[name] = component;
  return acc;
}, {} as Record<string, TextComponent>);
